import UtilSP from 'SATPROF_UTILS/UtilSP';
import Complex from 'SATPROF_UTILS/Complex';
import SigProc from 'SATPROF_UTILS/SigProc';

export default class Waveform1 {
/*
Contains a digital waveform with parameters defining number of points, center freq, freq step,
impedance, order (freq or FFT)
Has functions to convert between freq and time domain, power/phase, and I-Q

Methods:
clone(w:Waveform1)	
setToConstant( valRe:number, valIm:number, mode:string); //"freqDomainIQ" "timeDomainIQ"
    sets freq or time re and im arrays to valRe and valIm
addGain( gaindB, units:string, mode:string) // Units: "dB"  mode: "freqDomainIQ" "timeDomainIQ"
    adds gain to freq or time re and im arrays

addWaveform(w:Waveform1, mode:string) // mode: "freqDomainIQ" "timeDomainIQ"
    Sums in another waveform to either freq or time re and im arrays

addNoise(val:number, units:string, mode:string); // 
    Sums in random gaussian noise to the current freq re and im arrays
	Units: "noisePowerDensitydBmHz" "NFdB" "noiseTempK" 
	mode: "freqDomainIQ" MANDATORY
	If waveform freqDomainIQValid property is not valid, this method creates noise in the freq domain I/Q.

doFFTtime2freq(putInFreqOrder:boolean = true). 
    Generates freq re and im arrays from time re and im arrays.
doFFTfreq2time()
    Generates time re and im arrays from freq re and im arrays.
convertTimePowerPhase2IQ()
    Generates time power and phase arrays from time re and im arrays.
convertTimePowerPhase2IQ
    Generates time re and im arrays from time power and phase arrays.

everythingIsInvalid()
	
Properties:
N				number of points
freqDomainIsInFreqOrder
impedance
Fc
Fstep

These properties are all Array<number>, read/write.  Must have length N.  Req arrays are in Freq order (not FFT order)
freqDomainRe	
freqDomainIm	
timeDomainRe
timeDomainIm
timeDomainPwrdBm
timeDomainPhaseDeg
freqDomainPwrdBm		
freqDomainPhaseDeg	

These properties are all boolean, read-only.
freqDomainIQValid 
timeDomainIQValid 
timeDomainPwrPhaseValid
freqDomainIValid
freqDomainQValid
timeDomainIValid
timeDomainQValid
timeDomainPwrValid
timeDomainPhaseValid
freqDomainPwrValid 
freqDomainPhaseValid
		
	
	
*/
    private _freqDomainRe:Array<number>; 
    private _freqDomainIm:Array<number>;
    private _freqDomainIValid:boolean = false;
    private _freqDomainQValid:boolean = false;

    private _timeDomainRe:Array<number>; 
    private _timeDomainIm:Array<number>;
    private _timeDomainIValid:boolean = false;
    private _timeDomainQValid:boolean = false;

    private _timeDomainPwrdBm:Array<number>; 
    private _timeDomainPhaseDeg:Array<number>;
    private _timeDomainPwrValid:boolean = false;
    private _timeDomainPhaseValid:boolean = false;

    private _freqDomainPwrdBm:Array<number>; 
    private _freqDomainPhaseDeg:Array<number>; 
    private _freqDomainPwrValid:boolean = false;
    private _freqDomainPhaseValid:boolean = false;

    private _freqDomainIsInFreqOrder:boolean = true;
    private _impedance:number = 50;
    private _N = 0;
    private _Fc = 1e9; // Center frequency of the spectrum
    private _Fstep = 1e3; // Frequency step size of spectrum points
   
   constructor(n:number){
        this._N = n;
        this.regenerateAllVectors();
   }
   private regenerateAllVectors(){
        this._freqDomainRe = new Array<number>(this._N);
        this._freqDomainIm = new Array<number>(this._N);
        this._timeDomainRe = new Array<number>(this._N);
        this._timeDomainIm = new Array<number>(this._N);

        this._freqDomainPwrdBm = new Array<number>(this._N);
        this._freqDomainPhaseDeg = new Array<number>(this._N);
        this._timeDomainPwrdBm = new Array<number>(this._N);
        this._timeDomainPhaseDeg = new Array<number>(this._N);
        this.everythingIsInvalid();
    }
    public clone(w:Waveform1){
        // Make this Waveform1 a clone of w
        this._N = w.N;
        this._Fc = w.Fc;
        this._Fstep = w.Fstep;
        
        this.regenerateAllVectors();
        this._freqDomainIValid = w.freqDomainIValid;
        this._freqDomainQValid = w.freqDomainQValid;
        this._timeDomainIValid = w.timeDomainIValid;
        this._timeDomainQValid = w.timeDomainQValid;
        this._timeDomainPwrValid = w.timeDomainPwrValid;
        this._timeDomainPhaseValid = w.timeDomainPhaseValid;
        this._freqDomainPwrValid = w.freqDomainPwrValid;
        this._freqDomainPhaseValid = w.freqDomainPhaseValid;	
        this._impedance = w.impedance;
        this._freqDomainIsInFreqOrder = w.freqDomainIsInFreqOrder;
        
        var i:number;
        for (i=0; i<this._N; i++){
            this._freqDomainRe[i] = w.freqDomainRe[i];
            this._freqDomainIm[i] = w.freqDomainIm[i];
            this._timeDomainRe[i] = w.timeDomainRe[i];
            this._timeDomainIm[i] = w.timeDomainIm[i];

            this._freqDomainPwrdBm[i] = w.freqDomainPwrdBm[i];
            this._freqDomainPhaseDeg[i] = w.freqDomainPhaseDeg[i];
            this._timeDomainPwrdBm[i] = w.timeDomainPwrdBm[i];
            this._timeDomainPhaseDeg[i] = w.timeDomainPhaseDeg[i];
        }

    }

    public addGain( gaindB, units:string, mode:string){
        var i:number;
        if (units != "dB"){
            console.log("ERROR in Waveform1.addGain: units must be dB");
            return;
        }
        var g:number = UtilSP.dB2Vratio( gaindB );
        switch (mode){
            case "freqDomainIQ":
                if (!this.freqDomainIQValid){
                    console.log("ERROR in Waveform1.addGain: freqDomainIQValid = false. Cannot add gain.");
                    return;
                }
                for (i=0; i<this._N; i++){
                    this._freqDomainRe[i] *= g;
                    this._freqDomainIm[i] *= g;
                }
                this._freqDomainIValid = true;
                this._freqDomainQValid = true;
                this._timeDomainIValid = false;
                this._timeDomainQValid = false;
                this._timeDomainPwrValid = false;
                this._timeDomainPhaseValid = false;
                this._freqDomainPwrValid = false;
                this._freqDomainPhaseValid = false;
                break;
            case "timeDomainIQ":
                if (!this.timeDomainIQValid){
                    console.log("ERROR in Waveform1.addGain: timeDomainIQValid = false. Cannot add gain.");
                    return;
                }
                for (i=0; i<this._N; i++){
                    this._timeDomainRe[i] *= g;
                    this._timeDomainIm[i] *= g;
                }
                this._freqDomainIValid = false;
                this._freqDomainQValid = false;
                this._timeDomainIValid = true;
                this._timeDomainQValid = true;
                this._timeDomainPwrValid = false;
                this._timeDomainPhaseValid = false;
                this._freqDomainPwrValid = false;
                this._freqDomainPhaseValid = false;
                break;
            case "timeDomainPwrPhase":
                console.log("ERROR in Waveform1.addGain: timeDomainPwrPhase mode not implemented.");
                break;
            case "freqDomainPwrPhase":
                console.log("ERROR in Waveform1.addGain: freqDomainPwrPhase mode not implemented.");
                break;
        }
    }
    public setToConstant( valRe:number, valIm:number, mode:string){
        var i:number;
        switch (mode){
            case "freqDomainIQ":
                for (i=0; i<this._N; i++){
                    this._freqDomainRe[i] = valRe;
                    this._freqDomainIm[i] = valIm;
                }
                this._freqDomainIValid = true;
                this._freqDomainQValid = true;
                this._timeDomainIValid = false;
                this._timeDomainQValid = false;
                this._timeDomainPwrValid = false;
                this._timeDomainPhaseValid = false;
                this._freqDomainPwrValid = false;
                this._freqDomainPhaseValid = false;
                break;
            case "timeDomainIQ":
                for (i=0; i<this._N; i++){
                    this._timeDomainRe[i] = valRe;
                    this._timeDomainIm[i] = valIm;
                }
                this._freqDomainIValid = false;
                this._freqDomainQValid = false;
                this._timeDomainIValid = true;
                this._timeDomainQValid = true;
                this._timeDomainPwrValid = false;
                this._timeDomainPhaseValid = false;
                this._freqDomainPwrValid = false;
                this._freqDomainPhaseValid = false;
                break;
            case "timeDomainPwrPhase":
                console.log("ERROR in Waveform1.setToConstantGain: timeDomainPwrPhase mode not implemented.");
                break;
            case "freqDomainPwrPhase":
                console.log("ERROR in Waveform1.setToConstantGain: freqDomainPwrPhase mode not implemented.");
                break;
        }
    }		
    public addWaveform(w:Waveform1, mode:string){
        var i:number;
        if ( this._N != w.N || this._Fc != w.Fc || this._Fstep != w.Fstep){
            console.log("ERROR in Waveform1.addWaveform: Parameter mismatch. Current N ="+ this._N+", "+this._Fc+", Fstep = "+this._Fstep+". w N = "+ w.N+", Fc = "+w.Fc+", Fstep = "+w.Fstep);
            return;
        }
        switch (mode){
            case "freqDomainIQ":
                if (!this.freqDomainIQValid || !w.freqDomainIQValid){
                    console.log("ERROR in Waveform1.addWaveform: Cannot add waveform. Both waveforms must have freqDomainIQValid = true.");
                    return;
                }
                for (i=0; i<this._N; i++){
                    this._freqDomainRe[i] += w.freqDomainRe[i];
                    this._freqDomainIm[i] += w.freqDomainIm[i];
                }
                this._freqDomainIValid = true;
                this._freqDomainQValid = true;
                this._timeDomainIValid = false;
                this._timeDomainQValid = false;
                this._timeDomainPwrValid = false;
                this._timeDomainPhaseValid = false;
                this._freqDomainPwrValid = false;
                this._freqDomainPhaseValid = false;
                break;
            case "timeDomainIQ":
                if (!this.timeDomainIQValid || !w.timeDomainIQValid){
                    console.log("ERROR in Waveform1.addWaveform: Cannot add waveform. Both waveforms must have timeDomainIQValid = true.");
                    return;
                }
                for (i=0; i<this._N; i++){
                    this._timeDomainRe[i] += w.timeDomainRe[i];
                    this._timeDomainIm[i] += w.timeDomainIm[i];
                }
                this._freqDomainIValid = false;
                this._freqDomainQValid = false;
                this._timeDomainIValid = true;
                this._timeDomainQValid = true;
                this._timeDomainPwrValid = false;
                this._timeDomainPhaseValid = false;
                this._freqDomainPwrValid = false;
                this._freqDomainPhaseValid = false;
                break;
            case "timeDomainPwrPhase":
                console.log("ERROR in Waveform1.addWaveform: timeDomainPwrPhase mode not implemented.");
                break;
            case "freqDomainPwrPhase":
                console.log("ERROR in Waveform1.addWaveform: freqDomainPwrPhase mode not implemented.");
                break;
        }
    }

    public addNoise(val:number, units:string, mode:string){
        var noisePowerDensitydBmHz:number;
        var PSD:number;
        var Ptot:number;
        var span:number;
        var PinBW:number;
        var noise_sigma:number;
        var T:number;
        var F:number;
        var k:number = 1.38062E-23; // Botzmann's constant
        
        switch (units){
            case "noisePowerDensitydBmHz":
                noisePowerDensitydBmHz = val;
                break;
            case "NFdB":
                F = UtilSP.dBW2W(val);
                T = 290 * (F-1);
                noisePowerDensitydBmHz = UtilSP.W2dBW( k * T ) + 30;
                break;
            case "noiseTempK":
                T = val;
                noisePowerDensitydBmHz = UtilSP.W2dBW( k * T ) + 30;
                break;
            default:
                console.log("ERROR in Waveform1.addNoise: invalid units "+units);
                break;
        }
        PSD = Math.pow(10, (noisePowerDensitydBmHz - 30)/10); // Convert to Watts/Hz
        span = this._Fstep * this._N; // Total BW in Hz
        Ptot = PSD * span; // Total power in Watts in the entire spectral span
        PinBW = Ptot/this._N; // Power in Watts within one frequency step
        //noise_variance = PinBW * 50; //
        noise_sigma = Math.sqrt( PinBW * this._impedance / 2);
        //console.log("noise_sigma = "+noise_sigma);
        var i:number;
        switch (mode){
            case "freqDomainIQ":
                if (!this.freqDomainIQValid){
                    // Create from scratch
                    for (i=0; i<this._N; i++){
                        this._freqDomainRe[i] = noise_sigma * UtilSP.randGaussian();
                        this._freqDomainIm[i] = noise_sigma * UtilSP.randGaussian();
                    }
                } else {
                    // Add to what's already there.
                    for (i=0; i<this._N; i++){
                        this._freqDomainRe[i] += noise_sigma * UtilSP.randGaussian();
                        this._freqDomainIm[i] += noise_sigma * UtilSP.randGaussian();
                    }
                }
                this._freqDomainIValid = true;
                this._freqDomainQValid = true;
                this._timeDomainIValid = false;
                this._timeDomainQValid = false;
                this._timeDomainPwrValid = false;
                this._timeDomainPhaseValid = false;
                this._freqDomainPwrValid = false;
                this._freqDomainPhaseValid = false;
                break;
            case "timeDomainIQ":
                console.log("ERROR in Waveform1.addNoise: timeDomainIQ mode not implemented.");
                break;
            case "timeDomainPwrPhase":
                console.log("ERROR in Waveform1.addNoise: timeDomainPwrPhase mode not implemented.");
                break;
            case "freqDomainPwrPhase":
                console.log("ERROR in Waveform1.addNoise: freqDomainPwrPhase mode not implemented.");
                break;
        }
    }		


    public set freqDomainRe(val:Array<number>){
        if (val.length != this._N){
            console.log("ERROR in Waveform1.freqDomainRe!  length of argument vector ("+val.length+") does not match N ("+this._N+").");
        }
        this._freqDomainRe = val;
        
        this._freqDomainIValid = true;
        //this._freqDomainQValid = false;
        this._timeDomainIValid = false;
        this._timeDomainQValid = false;
        this._timeDomainPwrValid = false;
        this._timeDomainPhaseValid = false;
        this._freqDomainPwrValid = false;
        this._freqDomainPhaseValid = false;
    }
    public get freqDomainRe(){
        return this._freqDomainRe;
    }
    public set freqDomainIm(val:Array<number>){
        if (val.length != this._N){
            console.log("ERROR in Waveform1.freqDomainIm!  length of argument vector ("+val.length+") does not match N ("+this._N+").");
        }
        this._freqDomainIm = val;
        
        //this._freqDomainIValid = true;
        this._freqDomainQValid = true;
        this._timeDomainIValid = false;
        this._timeDomainQValid = false;
        this._timeDomainPwrValid = false;
        this._timeDomainPhaseValid = false;
        this._freqDomainPwrValid = false;
        this._freqDomainPhaseValid = false;
    }
    public get freqDomainIm(){
        return this._freqDomainIm;
    }
    public set timeDomainRe(val:Array<number>){
        if (val.length != this._N){
            console.log("ERROR in Waveform1.timeDomainRe!  length of argument vector ("+val.length+") does not match N ("+this._N+").");
        }
        this._timeDomainRe = val;

        this._freqDomainIValid = false;
        this._freqDomainQValid = false;
        this._timeDomainIValid = true;
        //this._timeDomainQValid = false;
        this._timeDomainPwrValid = false;
        this._timeDomainPhaseValid = false;
        this._freqDomainPwrValid = false;
        this._freqDomainPhaseValid = false;
        
        
    }
    public get timeDomainRe(){
        return this._timeDomainRe;
    }
    public set timeDomainIm(val:Array<number>){
        if (val.length != this._N){
            console.log("ERROR in Waveform1.timeDomainIm!  length of argument vector ("+val.length+") does not match N ("+this._N+").");
        }
        this._timeDomainIm = val;
        this._freqDomainIValid = false;
        this._freqDomainQValid = false;
        //this._timeDomainIValid = true;
        this._timeDomainQValid = true;
        this._timeDomainPwrValid = false;
        this._timeDomainPhaseValid = false;
        this._freqDomainPwrValid = false;
        this._freqDomainPhaseValid = false;
        
    }
    public get timeDomainIm(){
        return this._timeDomainIm;
    }

    public set timeDomainPwrdBm(val:Array<number>){
        if (val.length != this._N){
            console.log("ERROR in Waveform1.timeDomainPwrdBm!  length of argument vector ("+val.length+") does not match N ("+this._N+").");
        }
        this._timeDomainPwrdBm = val;
        this._freqDomainIValid = false;
        this._freqDomainQValid = false;
        this._timeDomainIValid = false;
        this._timeDomainQValid = false;
        this._timeDomainPwrValid = true;
        //this._timeDomainPhaseValid = false;
        this._freqDomainPwrValid = false;
        this._freqDomainPhaseValid = false;
    }
    public get timeDomainPwrdBm(){
        return this._timeDomainPwrdBm;
    }
    public set timeDomainPhaseDeg(val:Array<number>){
        if (val.length != this._N){
            console.log("ERROR in Waveform1.timeDomainPhaseDeg!  length of argument vector ("+val.length+") does not match N ("+this._N+").");
        }
        this._timeDomainPhaseDeg = val;
        this._freqDomainIValid = false;
        this._freqDomainQValid = false;
        this._timeDomainIValid = false;
        this._timeDomainQValid = false;
        //this._timeDomainPwrValid = true;
        this._timeDomainPhaseValid = true;
        this._freqDomainPwrValid = false;
        this._freqDomainPhaseValid = false;
    }
    public get timeDomainPhaseDeg(){
        return this._timeDomainPhaseDeg;
    }
    public set freqDomainPwrdBm(val:Array<number>){
        if (val.length != this._N){
            console.log("ERROR in Waveform1.freqDomainPwrdBm!  length of argument vector ("+val.length+") does not match N ("+this._N+").");
        }
        this._freqDomainPwrdBm = val;
        this._freqDomainIValid = false;
        this._freqDomainQValid = false;
        this._timeDomainIValid = false;
        this._timeDomainQValid = false;
        this._timeDomainPwrValid = false;
        this._timeDomainPhaseValid = false;
        this._freqDomainPwrValid = true;
        this._freqDomainPhaseValid = false;
        
    }
    public get freqDomainPwrdBm(){
        return this._freqDomainPwrdBm; // Vector
    }


    public set freqDomainPhaseDeg(val:Array<number>){
        if (val.length != this._N){
            console.log("ERROR in Waveform1.freqDomainPwrdBm!  length of argument vector ("+val.length+") does not match N ("+this._N+").");
        }
        this._freqDomainPhaseDeg = val;
        this._freqDomainIValid = false;
        this._freqDomainQValid = false;
        this._timeDomainIValid = false;
        this._timeDomainQValid = false;
        this._timeDomainPwrValid = false;
        this._timeDomainPhaseValid = false;
        //this._freqDomainPwrValid = true;
        this._freqDomainPhaseValid = true;
        
    }
    public get freqDomainPhaseDeg(){
        return this._freqDomainPhaseDeg; // Vector
    }

    //----------------------------------------
    //Conversions:
    public doFFTtime2freq(putInFreqOrder:boolean = true){
        if (!this.timeDomainIQValid){
            console.log("ERROR in Waveform1.doFFTtime2freq. timeDomainIQValid = false.  Cannot do FFT."); 
            return;
        }
        var xx:Array<number> = this._timeDomainRe.concat();
        var yy:Array<number> = this._timeDomainIm.concat();
        SigProc.fftCore( xx, yy, false);
        //Scale by this._N so that total input power (both sides of spectrum) = average output power
        for (var i:number = 0; i<this._N; i++){
            xx[i] /= this._N;
            yy[i] /= this._N;
        }

        this._freqDomainIsInFreqOrder = putInFreqOrder;
        if (putInFreqOrder){
            xx = SigProc.fftSwapAN(xx);
            yy = SigProc.fftSwapAN(yy);
        }
        this._freqDomainRe = xx.concat();
        this._freqDomainIm = yy.concat();

        this._freqDomainIValid = true;
        this._freqDomainQValid = true;
        this._timeDomainIValid = true;
        this._timeDomainQValid = true;
        this._timeDomainPwrValid = false;
        this._timeDomainPhaseValid = false;
        this._freqDomainPwrValid = false;
        this._freqDomainPhaseValid = false;

    }
    public doFFTfreq2time(){
        if (!this.freqDomainIQValid){
            console.log("ERROR in Waveform1.doFFTfreq2time. freqDomainIQValid = false.  Cannot do inverse FFT."); 
            return;
        }
        let xx:Array<number> = this._freqDomainRe.concat();
        let yy:Array<number> = this._freqDomainIm.concat();
        //console.log("xx input: = "+xx);
        if (this._freqDomainIsInFreqOrder){
            xx = SigProc.fftInverseSwapAN(xx);
            yy = SigProc.fftInverseSwapAN(yy);
        }
        //console.log("xx after swap: = "+xx);
        
        SigProc.fftCore( xx, yy, true); // Inverse FFT
        //Scale by this._N so that total input power (both sides of spectrum) = average output power
        for (var i:number = 0; i<this._N; i++){
            xx[i] *= this._N;
            yy[i] *= this._N;
        }
        
        //console.log("xx after inv fft: = "+xx);
        this._timeDomainRe = xx.concat();
        this._timeDomainIm = yy.concat();

        this._freqDomainIValid = true;
        this._freqDomainQValid = true;
        this._timeDomainIValid = true;
        this._timeDomainQValid = true;
        this._timeDomainPwrValid = false;
        this._timeDomainPhaseValid = false;
        this._freqDomainPwrValid = false;
        this._freqDomainPhaseValid = false;
        
    }
    public get status(){
        return(
        "fI="+this._freqDomainIValid+" fQ="+this._freqDomainQValid+
        " fPwr ="+this._freqDomainPwrValid+" fPh="+this._freqDomainPhaseValid+
        " tI="+this._timeDomainIValid+" tQ="+this._timeDomainQValid+
        " tPwr="+this._timeDomainPwrValid+" tPh="+this._timeDomainPhaseValid +"; p[0] = "+this._timeDomainPwrdBm[0]);
    }
    public convertTimeIQ2PowerPhase(){
        if (!this.timeDomainIQValid){
            console.log("ERROR in Waveform1.convertTimeIQ2PowerPhase.  timeDomainIQValid = false.  Cannot convert to power/phase."); 
            return;
        }
        var pW:number;
        var phaseRad:number;
        for (var i=0; i<this._N; i++){
            pW = (this._timeDomainRe[i]*this._timeDomainRe[i] + this._timeDomainIm[i]*this._timeDomainIm[i]) / this._impedance;
            this._timeDomainPwrdBm[i] = UtilSP.W2dBm( pW );	
            
            if ( this._timeDomainRe[i] == 0 && this._timeDomainIm[i] == 0 ){
                phaseRad = 0;
            } else {
                phaseRad = Math.atan2( this._timeDomainIm[i], this._timeDomainRe[i] );
            }
            this._timeDomainPhaseDeg[i] = UtilSP.degrees(phaseRad);
            if (i==0){
                //console.log("In convertTimeIQ2PowerPhase: "+this._timeDomainPwrdBm[i]);
            }
        }
        this._timeDomainPwrValid = true;
        this._timeDomainPhaseValid = true;
    }
    public convertTimePowerPhase2IQ(){
        if (!this.timeDomainPwrPhaseValid){
            console.log("ERROR in Waveform1.convertTimePowerPhase2IQ.  this.timeDomainPwrPhaseValid = false.  Cannot convert to I-Q."); 
            return;
        }
        var mag:number;
        var pW:number;
        var phaseRad:number;
        for (var i=0; i<this._N; i++){
            pW = UtilSP.dBm2W(this._timeDomainPwrdBm[i]);
            phaseRad = UtilSP.radians(this._timeDomainPhaseDeg[i]);
            mag = Math.sqrt( pW * this._impedance );
            this._timeDomainRe[i] = mag * Math.cos(phaseRad);
            this._timeDomainIm[i] = mag * Math.sin(phaseRad);
        }
        this._timeDomainIValid = true;
        this._timeDomainQValid = true;
    }

    // Validity checks:
    public get freqDomainIQValid(){
        return (this._freqDomainIValid && this._freqDomainQValid);
    }
    public get timeDomainIQValid(){
        return (this._timeDomainIValid && this._timeDomainQValid);
    }
    public get timeDomainPwrPhaseValid(){
        return (this._timeDomainPwrValid && this._timeDomainPhaseValid);
    }
    public set freqDomainIValid(val:boolean){
        this._freqDomainIValid = true;
    }
    public get freqDomainIValid(){
        return this._freqDomainIValid;
    }
    public set freqDomainQValid(val:boolean){
        this._freqDomainQValid = true;
    }
    public get freqDomainQValid(){
        return this._freqDomainQValid;
    }
    public get timeDomainIValid(){
        return this._timeDomainIValid;
    }
    public get timeDomainQValid(){
        return this._timeDomainQValid;
    }
    public set timeDomainPwrValid(val:boolean){
        this._timeDomainPwrValid = val;
    }
    public get timeDomainPwrValid(){
        return this._timeDomainPwrValid;
    }
    public set timeDomainPhaseValid(val:boolean){
        this._timeDomainPhaseValid = val;
    }
    public get timeDomainPhaseValid(){
        return this._timeDomainPhaseValid;
    }
    public get freqDomainPwrValid(){
        return this._freqDomainPwrValid;
    }
    public get freqDomainPhaseValid(){
        return this._freqDomainPhaseValid;
    }

    private everythingIsInvalid(){
        this._freqDomainIValid = false;
        this._freqDomainQValid = false;
        this._timeDomainIValid = false;
        this._timeDomainQValid = false;
        this._timeDomainPwrValid = false;
        this._timeDomainPhaseValid = false;
        this._freqDomainPwrValid = false;
        this._freqDomainPhaseValid = false;
    }

    public set freqDomainIsInFreqOrder(val:boolean){
        this._freqDomainIsInFreqOrder = val;
    }
    public get freqDomainIsInFreqOrder(){
        return this._freqDomainIsInFreqOrder;
    }
    public set impedance(val:number){
        this._impedance = val;
    }
    public get impedance(){
        return this._impedance;
    }
    public set N(val:number){
        if (val == this._N){
            return;
        }
        this._N = val;
        this.regenerateAllVectors();
    }
    public get N(){
        return this._N;
    }
    public set Fc(val:number){
        this._Fc = val;
    }
    public get Fc(){
        return this._Fc;
    }
    public set Fstep(val:number){
        this._Fstep = val;
    }
    public get Fstep(){
        return this._Fstep;
    }



} // End class